import {users as usersShared} from '@cl/shared';
import {CLSelectorOption} from 'common/src/components/Selectors/CLSelector/CLSelector.types';
import {
  AvailableDimension,
  AvailableFilter,
  AvailableMetricWithTotal,
} from 'common/src/modules/campaigns/store/insightsPage/insightsPage.types';
import {AgentContext} from 'common/src/modules/shared/copilot/copilot.types';
import {Dashboard} from 'common/src/modules/dashboards/dashboards.types';
import {doesUserHaveRole} from 'common/src/modules/roles/utils/doesUserHaveRole';
import {TimezoneObject} from 'common/src/modules/timezones/timezones.types';
import {RoleTypeName} from 'common/src/modules/users/users.types';
import {CLStore} from 'common/src/redux/store';
import _map from 'lodash/map';

type CampaignAnalyticsContext = {
  measuredPeriod: {
    activeSince: string;
    timezone: string; // Timezone Abbreviation
    timezoneOptions: (CLSelectorOption & TimezoneObject)[];
  };
  dashboards: Dashboard[];
  availableMetricsWithTotals: AvailableMetricWithTotal[];
  availableDimensions: AvailableDimension[];
  availableFilters: AvailableFilter[];
  isUserWithAnalyticsRole: boolean;
};

function campaignAnalyticsContext(store: CLStore): AgentContext<CampaignAnalyticsContext> {
  function getter(): CampaignAnalyticsContext {
    const insightsPage = store.getState().campaignPage.insightsPage;

    const isUserWithAnalyticsRole = doesUserHaveRole(
      usersShared.userSubject.data,
      RoleTypeName.AnalyticsWrite,
    );

    const {
      measuredPeriod,
      dashboards,
      availableMetricsWithTotals,
      availableDimensions,
      availableFilters,
    } = insightsPage;

    return {
      measuredPeriod,
      dashboards,
      availableMetricsWithTotals,
      availableDimensions,
      availableFilters,
      isUserWithAnalyticsRole,
    };
  }

  function formatter({currentContext}: {currentContext: CampaignAnalyticsContext}): string {
    if (!currentContext) {
      return '';
    }

    const {
      measuredPeriod,
      dashboards,
      availableMetricsWithTotals,
      availableDimensions,
      availableFilters,
      isUserWithAnalyticsRole,
    } = currentContext;

    if (!measuredPeriod.activeSince) {
      return `
      - Campaign was never active, no analytics data available
      - Campaign dashboards: ${_map(dashboards, (dashboard) => `${dashboard.name || 'Dashboard'} ID: ${dashboard.id} ${dashboard.readOnly ? '(Read Only)' : ''}`).join(', ')}
      `;
    } else {
      return `
      - User has analytics write role: ${isUserWithAnalyticsRole}
      - Available widgets and their limitations:
        1.Pie (up to 1 metric up to 1 dimension);
        2.Line (up to 2 metrics up to 1 dimension);
        3.Bar stacked (up to 2 metrics up to 1 dimension);
        4.Horizontal Bar (up to 2 metrics up to 1 dimension);
        5.Multi Table (up to 10 metrics and 3 dimensions).
      - Campaign is active since: ${measuredPeriod.activeSince}
      - Time zone: ${measuredPeriod.timezone}
      - Available metrics with totals since campaign was active: ${!availableMetricsWithTotals.length ? 'No Available metrics' : _map(availableMetricsWithTotals, (metric) => `${metric.name}: ${metric.total}`).join(', ')}
      - Available metrics: ${JSON.stringify(
        Object.fromEntries(availableMetricsWithTotals.map(({name, value}) => [name, value])),
      )}
      - Available viewing dimensions: ${JSON.stringify(
        Object.fromEntries(availableDimensions.map(({label, value}) => [label, value])),
      )}
      - Available filtering dimensions: ${JSON.stringify(
        Object.fromEntries(availableFilters.map(({label, value}) => [label, value])),
      )}
      - Campaign dashboards: ${_map(dashboards, (dashboard) => `${dashboard.name || 'Dashboard'} ID: ${dashboard.id} ${dashboard.readOnly ? '(Read Only)' : ''}`).join(', ')}
      `;
    }
  }

  return {
    title: 'Current campaign analytics',
    getter,
    formatter,
  };
}

export default campaignAnalyticsContext;
